<script setup>
import { computed, nextTick, ref, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { SchoolLevel } from '@/constants/accounts';

useSessionGuard();

// Subjects and the weight of each grade component. The school-wide admin sees
// both levels; the Junior/Senior High admins their own.
const store = useStore();
const roleLevel = store.getters.activeRoleLevel;
const TABS = [
  { id: 'Junior High', name: 'Junior High', level: SchoolLevel.Junior },
  { id: 'Senior High', name: 'Senior High', level: SchoolLevel.Senior },
].filter((tab) => !roleLevel || tab.level === roleLevel);
const level = shallowRef(TABS[0].id);
const isSenior = computed(() => level.value === 'Senior High');

const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', `/subjects/getSubject/active/${encodeURIComponent(level.value)}`),
);
watch(level, load, { immediate: true });

const headers = computed(() => [
  { title: 'Subject', key: 'subject_title' },
  { title: 'Teachers', key: 'coverage', sortable: false, hideable: true },
  ...(isSenior.value
    ? [
        { title: 'Grade', key: 'senior_level' },
        { title: 'Indicator', key: 'indicator' },
      ]
    : []),
  { title: 'Written', key: 'writen_works', align: 'end' },
  { title: 'Performance', key: 'performance_task', align: 'end' },
  { title: 'Quarterly', key: 'quarter_assessment', align: 'end' },
]);

// Who teaches each subject, per grade level, in the selected school year
// (from the class schedules). Shown when a subject's row is expanded.
const year = computed(() => store.getters.getFilterSelected);
const GRADES = {
  'Junior High': ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'],
  'Senior High': ['Grade 11', 'Grade 12'],
};
const gradeNumber = (g) => Number(String(g).replace(/\D/g, '')) || 0;
const teachers = shallowRef([]); // [{ subjectId, gradeLevel, teacherId, teacher, sections }]
const teachersLoading = shallowRef(false);
async function loadTeachers() {
  if (!year.value) return;
  teachersLoading.value = true;
  try {
    const rows = await apiRequest('get', `/subjects/teachers/${year.value}/${encodeURIComponent(level.value)}`);
    teachers.value = Array.isArray(rows) ? rows : [];
  } catch {
    teachers.value = [];
  } finally {
    teachersLoading.value = false;
  }
}
watch([level, year], loadTeachers, { immediate: true });

// The subject's grade levels (a Senior High subject set to one grade shows
// just that one, plus any other grade it is scheduled in), each with its
// teachers and their sections.
// `expected` is false for a grade the subject is scheduled in but doesn't
// belong to (e.g. a Junior High subject in a Grade 12 section), which is
// most likely a scheduling mistake.
function gradeRows(subject) {
  const mine = teachers.value.filter((t) => Number(t.subjectId) === Number(subject.id));
  const base = isSenior.value && subject.senior_level ? [subject.senior_level] : GRADES[level.value] ?? [];
  const grades = [...new Set([...base, ...mine.map((t) => t.gradeLevel)])].sort((a, b) => gradeNumber(a) - gradeNumber(b));
  return grades.map((grade) => ({
    grade,
    expected: base.includes(grade),
    teachers: mine.filter((t) => t.gradeLevel === grade),
  }));
}

// A period in a grade the subject doesn't belong to is fixed (edited or
// removed) on Classroom schedules, a sibling of this page in every role's
// area; the link opens it on that grade and section.
const route = useRoute();
const scheduleLink = (grade, section) => ({
  path: route.path.replace(/[^/]+$/, 'classroom-schedules'),
  query: { grade, section },
});

// As many rows as the grade level with the most teachers (at least one, so
// a subject with none still says so under each grade).
const teacherRowCount = (subject) => Math.max(1, ...gradeRows(subject).map((r) => r.teachers.length));

// The collapsed row's summary: whether every grade level the subject
// belongs to has a teacher (and which don't), and how many teachers it has.
function coverage(subject) {
  const rows = gradeRows(subject).filter((r) => r.expected);
  const open = rows.filter((r) => !r.teachers.length).map((r) => r.grade);
  const people = new Set(teachers.value.filter((t) => Number(t.subjectId) === Number(subject.id)).map((t) => t.teacherId)).size;
  if (!people) return { variant: 'outline', text: 'Not assigned', people };
  if (!open.length) return { variant: 'green', text: 'Fully assigned', people };
  // "Grade 9, 10" rather than "Grade 9, Grade 10"
  const grades = `Grade ${open.map((g) => String(g).replace(/^Grade\s*/i, '')).join(', ')}`;
  return { variant: 'amber', text: `No teacher for ${grades}`, people };
}

// Rows for the table, carrying the teachers' names so searching for a
// teacher finds the subjects they handle.
const rows = computed(() =>
  items.value.map((subject) => ({
    ...subject,
    teacherNames: teachers.value
      .filter((t) => Number(t.subjectId) === Number(subject.id))
      .map((t) => t.teacher)
      .join(' '),
  })),
);

const tracks = shallowRef([]);
const strands = shallowRef([]);
apiRequest('get', '/rooms-section/getAlltracks/Data/tracks').then((rows) => (tracks.value = rows || [])).catch(() => {});
apiRequest('get', '/rooms-section/AllStrand/Data/strand').then((rows) => (strands.value = rows || [])).catch(() => {});

// Senior High fields depend on the indicator and the school year's grading
// periods (4 quarters = syType 0, 3 terms = syType 1).
const syType = computed(() => Number(store.getters.getSyType ?? 0));
const showSemester = computed(() => isSenior.value && syType.value === 0);
const isElective = (v) => isSenior.value && syType.value === 1 && v.indicator === 'ELECTIVE';
const isSpecialized = (v) => isSenior.value && v.indicator === 'SPECIALIZED';
const percent = (v) => v === null || v === '' || (Number(v) >= 0 && Number(v) <= 100) || 'Enter 0 to 100.';
const options = (list) => list.map((v) => ({ title: v, value: v }));

const fields = computed(() => [
  { key: 'title', label: 'Subject title', required: true, span: 2 },
  { key: 'seniorLevel', label: 'Grade level', type: 'select', required: true, items: options(['Grade 11', 'Grade 12']), hidden: !isSenior.value },
  {
    key: 'indicator',
    label: 'Indicator',
    type: 'select',
    required: true,
    // A 3-term year still lists APPLIED and SPECIALIZED: SF10 groups Senior
    // High subjects by them.
    items: options(syType.value === 0 ? ['CORE', 'APPLIED', 'SPECIALIZED'] : ['CORE', 'APPLIED', 'SPECIALIZED', 'ELECTIVE']),
    hidden: !isSenior.value,
  },
  { key: 'semester', label: 'Semester', type: 'select', required: true, items: options(['First Semester', 'Second Semester']), hidden: !showSemester.value },
  { key: 'strandId', label: 'Strand', type: 'autocomplete', items: strands, itemTitle: 'strand_name', itemValue: 'id', hidden: (v) => !isSpecialized(v) },
  { key: 'trackId', label: 'Track', type: 'autocomplete', required: true, items: tracks, itemTitle: 'tracks_name', itemValue: 'id', hidden: (v) => !isElective(v) },
  { key: 'electiveNumber', label: 'Elective number', type: 'number', hidden: (v) => !isElective(v) },
  { key: 'writtenWorks', label: 'Written works (%)', type: 'number', required: true, rules: [percent], placeholder: '30' },
  { key: 'performanceTask', label: 'Performance task (%)', type: 'number', required: true, rules: [percent], placeholder: '50' },
  { key: 'quarterAssessment', label: 'Quarterly assessment (%)', type: 'number', required: true, rules: [percent], placeholder: '20' },
]);

const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({});

// Components, e.g. MAPEH's Music & Arts and Physical Education & Health:
// teachers grade each one with this subject's weights, and the subject's
// grade is their average. Saved as [{ id, description }]; new ones have no
// id until the server gives them one, and it refuses to drop a component
// that already has scores.
const components = ref([]); // [{ id, description, key }]
let componentKey = 0;
const newComponent = (id = null, description = '') => ({ id, description, key: ++componentKey });
function parseComponents(raw) {
  try {
    const list = JSON.parse(raw || '[]');
    return Array.isArray(list) ? list.map((c) => newComponent(c.id ?? null, c.description ?? '')) : [];
  } catch {
    return [];
  }
}
const hasComponents = computed(() => components.value.length > 0);
// A subject needs at least two components, so switching on starts with two.
function setHasComponents(on) {
  components.value = on ? [newComponent(), newComponent()] : [];
}
function addComponent() {
  components.value = [...components.value, newComponent()];
}
function removeComponent(key) {
  components.value = components.value.filter((c) => c.key !== key);
}
// e.g. "MAPEH grade = (Music & Arts + Physical Education & Health) ÷ 2"
const componentFormula = computed(() => {
  const names = components.value.map((c, i) => c.description.trim() || `Component ${i + 1}`);
  return `${form.value.title?.trim() || 'Subject'} grade = (${names.join(' + ')}) ÷ ${names.length}`;
});
// The weights each component is graded with: this subject's, as typed above.
const componentWeights = computed(() =>
  [
    ['Written', form.value.writtenWorks],
    ['Performance', form.value.performanceTask],
    ['Quarterly', form.value.quarterAssessment],
  ]
    .filter(([, v]) => v !== null && v !== '')
    .map(([label, v]) => `${label} ${v}%`)
    .join(' · '),
);
// One click for the most common case while nothing is named yet.
const MAPEH_PRESET = ['Music & Arts', 'Physical Education & Health'];
const canUseMapehPreset = computed(() => components.value.every((c) => !c.description.trim()));
function useMapehPreset() {
  components.value = MAPEH_PRESET.map((name) => newComponent(null, name));
}
// Enter in the last row adds the next component and moves to it.
async function onComponentEnter(index) {
  if (index !== components.value.length - 1 || !components.value[index].description.trim()) return;
  addComponent();
  await nextTick();
  document.getElementById(`subject-component-${components.value.at(-1).key}`)?.focus();
}
const componentsPayload = () =>
  JSON.stringify(
    components.value
      .filter((c) => c.description.trim())
      .map((c) => ({ id: c.id, description: c.description.trim() })),
  );
// Shown under the subject's name in the table.
const componentNames = (subject) =>
  parseComponents(subject.sub_subject)
    .map((c) => c.description)
    .filter(Boolean)
    .join(' · ');

function openForm(s = null) {
  editing.value = s;
  form.value = {
    title: s?.subject_title ?? '',
    writtenWorks: s?.writen_works ?? null,
    performanceTask: s?.performance_task ?? null,
    quarterAssessment: s?.quarter_assessment ?? null,
    seniorLevel: s?.senior_level ?? null,
    semester: s?.semester ?? null,
    indicator: s?.indicator ?? null,
    trackId: s?.trackID != null ? Number(s.trackID) : null,
    electiveNumber: s?.electiveNumber ?? null,
    strandId: s?.strandID != null ? Number(s.strandID) : null,
  };
  components.value = parseComponents(s?.sub_subject);
  formOpen.value = true;
}

const { busy, run } = useAsyncAction();
const { confirm } = useConfirm();
const toast = useToast();

async function save() {
  const f = form.value;
  if (hasComponents.value && components.value.filter((c) => c.description.trim()).length < 2) {
    toast.error('Name at least two components, or switch components off.');
    return;
  }
  const elective = isElective(f);
  const specialized = isSpecialized(f);
  const data = {
    subject_title: f.title.trim(),
    grade_level: level.value,
    writen_works: f.writtenWorks,
    performance_task: f.performanceTask,
    quarter_assessment: f.quarterAssessment,
    sub_subject: componentsPayload(),
    indicator: isSenior.value ? f.indicator : null,
    senior_level: isSenior.value ? f.seniorLevel : null,
    semester: showSemester.value ? f.semester : null,
    isSpecialized: specialized ? 1 : 0,
    strandID: specialized ? f.strandId : null,
    trackID: elective ? f.trackId : null,
    electiveNumber: elective ? f.electiveNumber : null,
  };
  const ok = await run(
    () => (editing.value ? apiRequest('patch', `/subjects/${editing.value.id}`, data) : apiRequest('post', '/subjects', data)),
    { success: editing.value ? 'Subject updated.' : 'Subject added.', error: 'Could not save the subject.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}

async function remove(subject) {
  const ok = await confirm({
    title: 'Remove subject?',
    description: `${subject.subject_title} will be removed from the active subjects.`,
    confirmText: 'Remove',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('patch', `/subjects/deleteSubject/${subject.id}`, {}), { success: 'Subject removed.', error: 'Could not remove the subject.' }))) {
    load();
  }
}
</script>

<template>
  <UiTableCard
    v-model:tab="level"
    title="Subjects"
    description="Subjects and the weight of each grade component."
    :tabs="TABS"
    :headers="headers"
    :items="rows"
    :search-fields="['teacherNames']"
    :loading="loading"
    :error="error"
    search-placeholder="Search subjects or teachers"
    add-label="Add subject"
    empty-text="No subjects yet."
    row-label="subject_title"
    :row-actions="(subject) => [
      { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(subject) },
      {
        title: 'Remove',
        icon: 'mdi-trash-can-outline',
        destructive: true,
        // A subject still in use stays: removing it would leave teachers and
        // class schedules pointing at a subject the school no longer lists.
        disabled: subject.inUse,
        reason: subject.inUse ? `In use: ${subject.usage}` : '',
        onClick: () => remove(subject),
      },
    ]"
    @add="openForm()"
  >
    <template #[`item.subject_title`]="{ item, internalItem, isExpanded, toggleExpand }">
      <button
        type="button"
        class="subject-toggle"
        :aria-expanded="isExpanded(internalItem)"
        :aria-label="`${isExpanded(internalItem) ? 'Hide' : 'Show'} teachers for ${item.subject_title}`"
        @click="toggleExpand(internalItem)"
      >
        <v-icon size="18" class="subject-toggle__icon">
          {{ isExpanded(internalItem) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
        </v-icon>
        <span>
          <span class="font-weight-medium">{{ item.subject_title }}</span>
          <span v-if="componentNames(item)" class="d-block text-caption text-medium-emphasis">
            {{ componentNames(item) }}
          </span>
        </span>
      </button>
    </template>
    <template #[`item.coverage`]="{ item }">
      <span v-if="teachersLoading" class="text-medium-emphasis">…</span>
      <span v-else class="coverage">
        <UiBadge :variant="coverage(item).variant">{{ coverage(item).text }}</UiBadge>
        <span v-if="coverage(item).people" class="coverage__people">
          {{ coverage(item).people }} teacher{{ coverage(item).people === 1 ? '' : 's' }}
        </span>
      </span>
    </template>
    <template #expanded-row="{ columns, item }">
      <tr class="subject-teachers">
        <td :colspan="columns.length">
          <div v-if="teachersLoading" class="text-medium-emphasis">Loading teachers…</div>
          <div v-else class="grade-table">
            <table>
              <thead>
                <tr>
                  <th v-for="col in gradeRows(item)" :key="col.grade" scope="col" :class="{ 'is-off': !col.expected }">
                    <span class="grade-table__head">
                      <span class="grade-table__grade">{{ col.grade }}</span>
                      <span
                        class="grade-table__count"
                        :title="`${col.teachers.length} teacher${col.teachers.length === 1 ? '' : 's'}`"
                        :aria-label="`${col.teachers.length} teacher${col.teachers.length === 1 ? '' : 's'}`"
                      >
                        {{ col.teachers.length }}
                      </span>
                    </span>
                    <span
                      v-if="!col.expected"
                      class="grade-table__off"
                      :title="`This is a ${level} subject. Open the section below to edit or remove its ${col.grade} schedule.`"
                    >
                      <UiBadge variant="red">Not {{ level }}</UiBadge>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="n in teacherRowCount(item)" :key="n">
                  <td v-for="col in gradeRows(item)" :key="col.grade">
                    <template v-if="col.teachers[n - 1]">
                      <span class="grade-table__name">{{ col.teachers[n - 1].teacher }}</span>
                      <span v-if="!col.expected && col.teachers[n - 1].sections.length" class="grade-table__section">
                        <template v-for="(sec, i) in col.teachers[n - 1].sections" :key="sec">
                          <template v-if="i">, </template>
                          <router-link
                            :to="scheduleLink(col.grade, sec)"
                            class="grade-table__link"
                            :title="`Open the ${col.grade} · ${sec} schedule`"
                          >{{ sec }}</router-link>
                        </template>
                      </span>
                      <span v-else class="grade-table__section">
                        {{ col.teachers[n - 1].sections.join(', ') || 'No section' }}
                      </span>
                    </template>
                    <span v-else-if="n === 1" class="grade-table__none">No teacher assigned</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit subject' : 'Add subject'"
    :description="level"
    size="lg"
    layout="grid"
    v-model:values="form"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add subject'"
    @submit="save"
  >
    <section class="components span-2" :class="{ 'components--on': hasComponents }" aria-labelledby="components-title">
      <label class="components__head" for="subject-has-components">
        <span class="components__icon"><v-icon size="18">mdi-format-list-group</v-icon></span>
        <span class="components__text">
          <span id="components-title" class="components__title">This subject has components</span>
          <span class="components__hint">Graded separately, like MAPEH's Music &amp; Arts and PE &amp; Health.</span>
        </span>
        <v-switch
          id="subject-has-components"
          :model-value="hasComponents"
          color="primary"
          inset
          hide-details
          density="compact"
          class="components__switch"
          @update:model-value="setHasComponents"
        />
      </label>

      <div v-if="hasComponents" class="components__body">
        <div class="components__bar">
          <span v-if="componentWeights" class="components__weights">
            <v-icon size="14">mdi-scale-balance</v-icon>
            Each uses {{ componentWeights }}
          </span>
          <v-btn
            v-if="canUseMapehPreset"
            variant="text"
            size="small"
            prepend-icon="mdi-auto-fix"
            class="components__preset"
            @click="useMapehPreset"
          >
            Use MAPEH's components
          </v-btn>
        </div>

        <ol class="components__list">
          <li v-for="(c, index) in components" :key="c.key" class="components__row">
            <span class="components__num" aria-hidden="true">{{ index + 1 }}</span>
            <v-text-field
              :id="`subject-component-${c.key}`"
              v-model="c.description"
              :aria-label="`Component ${index + 1} name`"
              :placeholder="MAPEH_PRESET[index] ? `e.g. ${MAPEH_PRESET[index]}` : 'Component name'"
              hide-details
              @keydown.enter.prevent="onComponentEnter(index)"
            />
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              :disabled="components.length <= 2"
              :aria-label="`Remove ${c.description || `component ${index + 1}`}`"
              :title="components.length <= 2 ? 'A subject with components needs at least two' : 'Remove'"
              @click="removeComponent(c.key)"
            />
          </li>
        </ol>

        <v-btn variant="text" size="small" prepend-icon="mdi-plus" class="components__add" @click="addComponent">
          Add component
        </v-btn>

        <div class="components__summary">
          <v-icon size="16">mdi-function-variant</v-icon>
          <span>{{ componentFormula }}</span>
        </div>
        <p v-if="editing" class="components__note">A component that already has scores can be renamed but not removed.</p>
      </div>
    </section>
  </UiFormDialog>
</template>

<style scoped>
/* Subject name doubles as the toggle for its grade levels and teachers */
.subject-toggle {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.subject-toggle__icon {
  margin-top: 1px;
  color: var(--muted-foreground);
}
.subject-toggle:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
  border-radius: 4px;
}
/* The expanded row is a tinted well, so the white table inside stands out */
.subject-teachers > td {
  padding: 16px !important;
  background: color-mix(in srgb, var(--foreground) 4%, var(--card));
}
.coverage {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.coverage__people {
  font-size: 13px;
  color: var(--muted-foreground);
}
/* Scrolls inside its own frame, so the grade header stays in view while
   a long list of teachers scrolls under it. */
.grade-table {
  max-height: 320px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
}
.grade-table table {
  width: 100%;
  /* separate, not collapse: collapsed borders don't travel with a sticky header */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 13px;
}
.grade-table th,
.grade-table td {
  min-width: 180px;
  padding: 12px;
  border-bottom: 1px solid var(--border);
  text-align: center;
  vertical-align: middle;
}
.grade-table tbody tr:last-child td {
  border-bottom: 0;
}
.grade-table th + th,
.grade-table td + td {
  border-left: 1px solid var(--border);
}
.grade-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  /* A solid brand tint (sticky cells must be opaque), so it reads apart from
     both the white rows and the grey well around the table */
  background: color-mix(in srgb, var(--brand) 10%, var(--card));
  border-bottom-color: var(--border-strong);
}
.grade-table thead th.is-off {
  box-shadow: inset 0 -2px 0 rgba(220, 38, 38, 0.6);
}
.grade-table__head {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.grade-table__grade {
  font-weight: 600;
  color: var(--brand);
}
.grade-table__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  border-radius: 9999px;
  background: var(--card);
  border: 1px solid color-mix(in srgb, var(--brand) 35%, var(--card));
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--brand);
}
.grade-table__off {
  display: block;
  margin-top: 6px;
}
.grade-table__name {
  display: block;
  font-weight: 500;
}
.grade-table__section {
  display: block;
  font-size: 12px;
  color: var(--muted-foreground);
}
.grade-table__link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-underline-offset: 2px;
}
.grade-table__none {
  color: var(--muted-foreground);
}
.components {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  transition: border-color 0.15s ease;
}
.components--on {
  border-color: var(--border-strong);
}
.components__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 12px 12px 14px;
  cursor: pointer;
}
.components__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--accent);
  color: var(--secondary-foreground);
}
.components--on .components__icon {
  background: var(--brand-soft);
  color: var(--brand);
}
.components__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.components__title {
  font-size: 14px;
  font-weight: 500;
}
.components__hint,
.components__note,
.components__weights {
  font-size: 13px;
  color: var(--muted-foreground);
}
.components__switch {
  flex: none;
}
.components__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px 14px;
  border-top: 1px solid var(--border);
  background: var(--subtle);
}
.components__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
  min-height: 28px;
}
.components__weights {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-variant-numeric: tabular-nums;
}
.components__preset {
  margin-left: auto;
}
.components__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.components__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.components__row :deep(.v-field) {
  background: var(--card);
}
.components__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: var(--card);
  border: 1px solid var(--border);
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--secondary-foreground);
}
.components__add {
  align-self: flex-start;
  margin-left: 24px;
}
.components__summary {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed var(--border-strong);
  border-radius: 8px;
  background: var(--card);
  font-size: 13px;
  color: var(--secondary-foreground);
  overflow-wrap: anywhere;
}
.components__note {
  margin: 0;
}
</style>
